import { ExtractComponentProp } from '@approvalmax/types';
import { Popup, useConfirmation } from '@approvalmax/ui/src/components';
import { useAtomValue } from 'jotai';
import { memo, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { useMatchXeroPopupQueryParams } from '../../../resources/hooks/useMatchXeroPopupQueryParams';
import { FieldValues, MatchContent, ReconnectOrgPopup, reconnectOrgPopupOpenState, ReMatchContent } from './components';
import { useMatchingPopupData } from './MatchBankAccountWithXeroPopup.hooks';
import { messages } from './MatchBankAccountWithXeroPopup.messages';

export const MatchBankAccountWithXeroPopup = memo(function MatchBankAccountWithXeroPopup() {
    const { companyId } = useParams<{ companyId: string }>();
    const { consentId, cleanConsentId, bankAccountId, cleanBankAccountId, reMatch, cleanReMatch } =
        useMatchXeroPopupQueryParams();
    const [openForm, setOpenForm] = useState(false);
    const [somethingWasMatched, setSomethingWasMatched] = useState(false);
    const reconnectOrgPopupOpen = useAtomValue(reconnectOrgPopupOpenState);

    const form = useForm<FieldValues>();

    const {
        bankAccounts,
        isLoading,
        refetchBankAccounts,
        xeroBankAccounts,
        isLoadingXeroBankAccounts,
        someButNotAllBankAccountsWereMatched,
        allBankAccountsWereMatched,
    } = useMatchingPopupData({ companyId, consentId, bankAccountId, reMatch: Boolean(reMatch) });

    const { confirmation } = useConfirmation({
        confirmMessage: someButNotAllBankAccountsWereMatched ? messages.notAllMatchedAreYouSure : messages.areYouSure,
        confirmButtonMessage: messages.yesSkip,
    });

    const handleOpenForm = useCallback(() => {
        setOpenForm(true);
    }, []);

    const flushPopup = useCallback(() => {
        setOpenForm(false);
        setSomethingWasMatched(false);
        form.reset();
        cleanConsentId();
        cleanBankAccountId();
        cleanReMatch();
    }, [cleanBankAccountId, cleanConsentId, cleanReMatch, form]);

    useEffect(() => {
        // close popup only in case when matching was done manually
        if (somethingWasMatched && allBankAccountsWereMatched) {
            flushPopup();
        }
    }, [allBankAccountsWereMatched, flushPopup, somethingWasMatched]);

    useEffect(() => {
        // when no bank accounts within xero, open creation form immediately
        if (consentId && !isLoadingXeroBankAccounts && xeroBankAccounts.length === 0) {
            handleOpenForm();
        }
    }, [consentId, handleOpenForm, isLoadingXeroBankAccounts, xeroBankAccounts.length]);

    const togglePopup = useCallback<ExtractComponentProp<typeof Popup, 'onToggle'>>(
        (open) => {
            if (!open && !reconnectOrgPopupOpen) {
                void confirmation()
                    .then(flushPopup)
                    .catch(() => {});
            }
        },
        [confirmation, flushPopup, reconnectOrgPopupOpen]
    );

    const handleMatch = useCallback(() => {
        setSomethingWasMatched(true);
    }, []);

    return (
        <>
            <Popup className='fs-exclude' open={Boolean(consentId)} onToggle={togglePopup}>
                {reMatch ? (
                    <ReMatchContent
                        companyId={companyId}
                        openForm={openForm}
                        setOpenForm={setOpenForm}
                        form={form}
                        bankAccounts={bankAccounts}
                        isLoading={isLoading}
                        xeroBankAccounts={xeroBankAccounts}
                        isLoadingXeroBankAccounts={isLoadingXeroBankAccounts}
                        refetchBankAccounts={refetchBankAccounts}
                        onMatch={handleMatch}
                    />
                ) : (
                    <MatchContent
                        companyId={companyId}
                        openForm={openForm}
                        setOpenForm={setOpenForm}
                        form={form}
                        bankAccounts={bankAccounts}
                        isLoading={isLoading}
                        xeroBankAccounts={xeroBankAccounts}
                        isLoadingXeroBankAccounts={isLoadingXeroBankAccounts}
                        refetchBankAccounts={refetchBankAccounts}
                        onMatch={handleMatch}
                    />
                )}
            </Popup>

            <ReconnectOrgPopup />
        </>
    );
});

MatchBankAccountWithXeroPopup.displayName = 'MatchBankAccountWithXeroPopup';
